import React, { useEffect, useReducer, useRef, useState } from 'react';

const initialState = {
  connectors: [],
  strategies: [],
  selectedStrategy: null,
  strategyDetails: null,
  wsConnected: false
};

// Reducer function
function robotReducer(state, action) {
  // console.log('Reducer action:', action);
  switch (action.type) {
    case 'SET_CONNECTORS':
      return { ...state, connectors: action.payload };
    case 'SET_STRATEGIES':
      // Ensure we maintain the selected strategy if it exists in the new data
      const selectedStrategy = state.selectedStrategy &&
        action.payload.find(s => s.name === state.selectedStrategy) ?
        state.selectedStrategy : null;
      return {
        ...state,
        strategies: action.payload,
        selectedStrategy
      };
    case 'SELECT_STRATEGY':
      return { ...state, selectedStrategy: action.payload };
    case 'SET_STRATEGY_DETAILS':
      return { ...state, strategyDetails: action.payload };
    case 'WS_CONNECTED':
      return { ...state, wsConnected: true };
    case 'WS_DISCONNECTED':
      return { ...state, wsConnected: false };
    default:
      return state;
  }
}


function DataDisplay({token}) {
  const [state, dispatch] = useReducer(robotReducer, initialState);
  const [restartClicks, setRestartClicks] = useState(0);
  const { connectors, strategies, selectedStrategy, strategyDetails, wsConnected } = state;
  const wsRef = useRef(null);

  useEffect(() => {
    // Connect to WebSocket
    const ws = new WebSocket('wss://uhf88888.ru/ws');
    // const ws = new WebSocket('ws://192.168.0.8:8000/ws');
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
      type: "auth",
      token: token
    }));
      dispatch({ type: 'WS_CONNECTED' });
    }

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      // console.log('Message received:', data);
      switch (data.section) {
        case 'connectors':
          dispatch({ type: 'SET_CONNECTORS', payload: data.data });
          break;
        case 'strategies':
          dispatch({ type: 'SET_STRATEGIES', payload: data.data });
          break;
        case 'strategy_details':
          dispatch({ type: 'SET_STRATEGY_DETAILS', payload: data.data });
          break;
        default:
          console.log('Unknown message type:', data);
      }
    };

    ws.onerror = (err) => {
      console.error("Websocket Error: ", err);
      dispatch({ type: 'WS_DISCONNECTED' });
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      dispatch({ type: 'WS_DISCONNECTED' });
    };

    return () => {
      ws.close();
    };
  }, [token]);

  const handleStrategySelect = (strategyName) => {
    dispatch({ type: 'SELECT_STRATEGY', payload: strategyName });
    // Request strategy details
    sendCommand('get_strategy_details', { strategy_name: strategyName });
  };

  const sendCommand = (commandType, payload = {}) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected');
      return;
    }

    const message = {
      type: 'command',
      command: commandType,
      ...payload,
      timestamp: new Date().toISOString()
    };

    try {
      wsRef.current.send(JSON.stringify(message));
      console.log('Command sent:', message);
    } catch (error) {
      console.error('Failed to send command:', error);
    }
  };

  const handleRestartClick = () => {
    const newClickCount = restartClicks + 1;

    if (newClickCount >= 3) {
      sendCommand('restart_robot');
      setRestartClicks(0);
    } else {
      setRestartClicks(newClickCount);
    }
  };

  const controlConnector = (connectorName, action) => {
    sendCommand('control_connector', {
      connector_name: connectorName,
      action: action
    });
  };

  const controlStrategy = (strategyName, action) => {
    sendCommand('control_strategy', {
      strategy_name: strategyName,
      action: action
    });
  };

  return (
      <div className="dashboard">
        <div className="connection-controls">
          <div className="status-indicator">
            WebSocket: {wsConnected ? (
              <span className="status-connected">Connected</span>
          ) : (
              <span className="status-disconnected">Disconnected</span>
          )}
          </div>
          <button
              className="restart-button"
              onClick={handleRestartClick}
          >
            Restart Robot {3 - restartClicks}
          </button>
        </div>

        <div className="dashboard-section">
          <h2>Connectors</h2>
          <div className="connectors-list">
            {connectors.map(connector => (
                <div key={connector.name} className="connector-item">
                  <div className="connector-status">
                <span
                    className="status-circle"
                    style={{backgroundColor: connector.status === 'online' ? 'green' : 'red'}}
                />
                    {connector.name}
                  </div>
                  <div className="connector-actions">
                    <button
                        onClick={() => controlConnector(connector.name, 'start')}
                        disabled={connector.status === 'online'}
                    >
                      Start
                    </button>
                    <button
                        onClick={() => controlConnector(connector.name, 'stop')}
                        disabled={connector.status === 'offline'}
                    >
                      Stop
                    </button>
                  </div>
                </div>
            ))}
          </div>
        </div>

        <div className="dashboard-section">
          <h2>Strategies</h2>
          <div className="strategies-list">
            {strategies.map(strategy => (
                <div
                    key={strategy.name}
                    className={`strategy-item ${selectedStrategy === strategy.name ? 'selected' : ''}`}
                    onClick={() => handleStrategySelect(strategy.name)}
                >
                  <div className="strategy-status">
                <span
                    className="status-circle"
                    style={{backgroundColor: strategy.status === 'running' ? 'green' : 'red'}}
                />
                    {strategy.name}
                  </div>
                  <div className="strategy-actions">
                    <button
                        onClick={(e) => {
                          e.stopPropagation();
                          controlStrategy(strategy.name, 'start');
                        }}
                        disabled={strategy.status === 'running'}
                    >
                      Start
                    </button>
                    <button
                        onClick={(e) => {
                          e.stopPropagation();
                          controlStrategy(strategy.name, 'stop');
                        }}
                        disabled={strategy.status === 'stopped'}
                    >
                      Stop
                    </button>
                  </div>
                </div>
            ))}
          </div>
        </div>

        {selectedStrategy && (
            <div className="dashboard-section strategy-details">
              <h2>Strategy Details: {selectedStrategy}</h2>
              {strategyDetails ? (
                  <div className="strategy-details-content">
                    {/* Market Data Section */}
                    <div className="market-data">
                      <h3>Market Data</h3>
                      <div className="market-data-values">
                        <div className="market-data-row">
                          <span className="market-data-label">Ask:</span>
                          <span className="market-data-value">{strategyDetails.market_data.ask}</span>
                        </div>
                        <div className="market-data-row">
                          <span className="market-data-label">Bid:</span>
                          <span className="market-data-value">{strategyDetails.market_data.bid}</span>
                        </div>
                      </div>
                    </div>

                    {/* Indicators Section */}
                    <div className="indicators">
                      <h3>Indicators</h3>
                      <div className="indicators-grid">
                        {Object.entries(strategyDetails.indicators).map(([name, value]) => (
                            <div key={name} className="indicator-item">
                              <span className="indicator-name">{name}:</span>
                              <span className="indicator-value">{value}</span>
                            </div>
                        ))}
                      </div>
                    </div>

                    {/* Orders Section */}
                    <div className="orders">
                      <h3>Active Orders</h3>
                      {strategyDetails.orders.length > 0 ? (
                          <ul className="orders-list">
                            <li key={0} className="order-item">
                              <span className="order-deal_id">DealID</span>
                              <span className="order-trans_id">TransID</span>
                              <span className="order-datetime">Created</span>
                              <span className="order-quantity">Qty</span>
                              <span className="order-direction">Direction</span>
                              <span className="order-price">Price</span>
                              <span className="order-status">Status</span>
                              <span className="order-purpose">Purpose</span>
                            </li>
                            {strategyDetails.orders.map((order, index) => (
                                <li key={index + 1} className="order-item">
                                  <span className="order-deal_id">{order.deal_id}</span>
                                  <span className="order-trans_id">{order.trans_id}</span>
                                  <span className="order-datetime">{order.dt_created.substring(5, 16)}</span>
                                  <span className="order-quantity">{order.quantity}</span>
                                  <span className="order-direction">{order.direction}</span>
                                  <span className="order-price">{order.price}</span>
                                  <span className="order-status">{order.status}</span>
                                  <span className="order-purpose">{order.params.purpose}</span>
                                </li>
                            ))}
                          </ul>
                      ) : (
                          <p>No active orders</p>
                      )}
                    </div>

                    {/* Positions Section */}
                    <div className="positions">
                      <h3>Open Positions</h3>
                      {strategyDetails.positions.length > 0 ? (
                          <ul className="positions-list">
                            <li key={0} className="position-item">
                              <span className="position-deal_id">DealID</span>
                              <span className="position-deal_date">Date</span>
                              <span className="position-direction_type">Direction</span>
                              <span className="position-entry_price">Entry</span>
                              <span className="position-exit_price">Exit</span>
                              <span className="position-tp_price">TP</span>
                              <span className="position-sl_price">SL</span>
                              <span className="position-vol_opening">VolOpen</span>
                              <span className="position-vol_closing">VolClos</span>
                              <span className={"position-pnl_gross"}>PnL Closed</span>
                              <span className={"position-pnl_live"}>PnL Open</span>

                            </li>
                            {strategyDetails.positions.map((position, index) => (
                                <li key={index + 1} className="position-item">
                                  <span className="position-deal_id">{position.deal_id}</span>
                                  <span className="position-deal_date">{position.deal_date}</span>
                                  <span className="position-direction_type">{position.direction_type}</span>
                                  <span className="position-entry_price">{position.entry_price}</span>
                                  <span className="position-exit_price">{position.exit_price}</span>
                                  <span className="position-tp_price">{position.tp_price}</span>
                                  <span className="position-sl_price">{position.sl_price}</span>
                                  <span className="position-vol_opening">{position.vol_opening}</span>
                                  <span className="position-vol_closing">{position.vol_closing}</span>
                                  <span
                                      className={`position-pnl_gross ${position.pnl_gross >= 0 ? 'positive' : 'negative'}`}>
                          {position.pnl_gross}
                        </span>
                                  <span
                                      className={`position-pnl_live ${position.pnl_live >= 0 ? 'positive' : 'negative'}`}>
                          {position.pnl_live}
                        </span>

                                </li>
                            ))}
                          </ul>
                      ) : (
                          <p>No open positions</p>
                      )}
                    </div>

                  </div>
              ) : (
                  <p>Loading strategy details...</p>
              )}
            </div>
        )}
      </div>
  );
}

export default DataDisplay;